import { useEffect, useState } from "react"

import {
  getMyProfileApi,
  updateMyProfileApi,
} from "../../../features/profile/api/profile.api"

import {
  listMyExperiencesApi,
  createExperienceApi,
  deleteExperienceApi,
} from "../../../features/profile/api/experience.api"

import {
  listMyEducationsApi,
  createEducationApi,
  deleteEducationApi,
} from "../../../features/profile/api/education.api"

import {
  listMyCertificatesApi,
  createCertificateApi,
  deleteCertificateApi,
} from "../../../features/profile/api/certificate.api"

import {
  listMyPortfolioApi,
  uploadPortfolioItemApi,
} from "../../../features/profile/api/portfolio.api"

import {
  UserProfile,
  Experience,
  Education,
  Certificate,
  Media,
} from "../types"

export const useProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null)

  const [experiences, setExperiences] = useState<Experience[]>([])
  const [educations, setEducations] = useState<Education[]>([])
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [portfolio, setPortfolio] = useState<Media[]>([])

  const [loading, setLoading] = useState(true)

  /* ===================== LOAD ALL ===================== */

  useEffect(() => {
    Promise.all([
      getMyProfileApi(),
      listMyExperiencesApi(),
      listMyEducationsApi(),
      listMyCertificatesApi(),
      listMyPortfolioApi(),
    ]).then(([p, e, ed, c, port]) => {
      setProfile(p.data)
      setExperiences(e.data.items)
      setEducations(ed.data.items)
      setCertificates(c.data.items)
      setPortfolio(port.data)
      setLoading(false)
    })
  }, [])

  /* ===================== PROFILE ===================== */

  const updateProfile = (data: Partial<UserProfile>) =>
    updateMyProfileApi(data).then(() =>
      setProfile((prev) => {
        if (!prev) return prev
        return { ...prev, ...data }
      })
    )

  /* ===================== EXPERIENCE ===================== */

  const addExperience = (item: Omit<Experience, "id">) =>
    createExperienceApi(item).then((r) =>
      setExperiences((p) => [...p, r.data])
    )

  const deleteExperience = (id: string) =>
    deleteExperienceApi(id).then(() =>
      setExperiences((p) => p.filter((i) => i.id !== id))
    )

  /* ===================== EDUCATION ===================== */

  const addEducation = (item: Omit<Education, "id">) =>
    createEducationApi(item).then((r) =>
      setEducations((p) => [...p, r.data])
    )

  const deleteEducation = (id: string) =>
    deleteEducationApi(id).then(() =>
      setEducations((p) => p.filter((i) => i.id !== id))
    )

  /* ===================== CERTIFICATE ===================== */

  const addCertificate = (item: Omit<Certificate, "id">) =>
    createCertificateApi(item).then((r) =>
      setCertificates((p) => [...p, r.data])
    )

  const deleteCertificate = (id: string) =>
    deleteCertificateApi(id).then(() =>
      setCertificates((p) => p.filter((i) => i.id !== id))
    )

  /* ===================== PORTFOLIO ===================== */

  const addPortfolioItem = (file: File) =>
    uploadPortfolioItemApi(file).then((r) =>
      setPortfolio((p) => [r.data, ...p])
    )

  return {
    /* state */
    profile,
    experiences,
    educations,
    certificates,
    portfolio,
    loading,

    /* profile */
    updateProfile,

    /* experience */
    addExperience,
    deleteExperience,

    /* education */
    addEducation,
    deleteEducation,

    /* certificate */
    addCertificate,
    deleteCertificate,

    /* portfolio */
    addPortfolioItem,
  }
}
